import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/lib/routes';
import { X, ArrowRight, RefreshCw } from 'lucide-react';
import { useTeamStore } from '@/store/useTeamStore';
import { useChatStore } from '@/store/useChatStore';
import type { TeamMember } from '@/types';
import { CharacterPortrait } from './CharacterPortrait';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, VoucherRequiredError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/useAuthStore';
import { LoginPromptDialog } from '@/components/auth/LoginPromptDialog';
import { VoucherPromptDialog } from '@/components/auth/VoucherPromptDialog';

type PortraitSize = 'md' | 'xl' | 'lg';

function getBubbleSize(member: TeamMember): PortraitSize {
  const score = member.values.length + member.typicalPhrases.length;
  if (score >= 8) return 'lg';
  if (score >= 4) return 'xl';
  return 'md';
}

export function TeamBubbleView() {
  const { members, updateMember } = useTeamStore();
  const sessions = useChatStore((s) => s.sessions);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [generatingAvatarId, setGeneratingAvatarId] = useState<string | null>(null);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [voucherPromptOpen, setVoucherPromptOpen] = useState(false);

  const selected = members.find((m) => m.id === selectedId) ?? null;

  async function handleRegenerateAvatar(memberId: string) {
    if (!useAuthStore.getState().user) { setLoginPromptOpen(true); return; }
    setGeneratingAvatarId(memberId);
    try {
      const linkedSession = sessions
        .filter((s) => s.memberIds.includes(memberId))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
      const result = await api.avatar.regenerate(memberId, linkedSession?.id);
      updateMember(memberId, {
        avatarImageUrl: result.avatarImageUrl,
        appearanceDescription: result.appearanceDescription,
      });
      toast.success('Avatar neu generiert!');
    } catch (err) {
      if (err instanceof VoucherRequiredError) {
        setVoucherPromptOpen(true);
      } else {
        toast.error('Avatar-Generierung fehlgeschlagen.');
      }
    } finally {
      setGeneratingAvatarId(null);
    }
  }

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelectedId(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock body scroll while sheet is open
  useEffect(() => {
    document.body.style.overflow = selectedId ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedId]);

  function handleBubbleClick(id: string) {
    setSelectedId((prev) => (prev === id ? null : id));
  }

  return (
    <>
      {/* Bubble grid */}
      <div className="flex flex-wrap justify-center items-center gap-5 px-6 py-8 content-start">
        {members.map((member) => {
          const size = getBubbleSize(member);
          const isSelected = member.id === selectedId;
          const isDimmed = selectedId !== null && !isSelected;

          return (
            <motion.button
              key={member.id}
              onClick={() => handleBubbleClick(member.id)}
              animate={{ opacity: isDimmed ? 0.45 : 1, scale: isSelected ? 1.06 : 1 }}
              transition={{ duration: 0.2 }}
              whileTap={{ scale: 0.93 }}
              className="flex flex-col items-center gap-2 focus:outline-none"
              aria-label={member.name}
            >
              <div
                className="rounded-full transition-all"
                style={
                  isSelected
                    ? {
                        outline: '3px solid var(--primary)',
                        outlineOffset: 4,
                      }
                    : undefined
                }
              >
                <CharacterPortrait
                  emoji={member.avatarEmoji}
                  color={member.avatarColor}
                  avatarImageUrl={member.avatarImageUrl}
                  size={size}
                  onImageError={() => updateMember(member.id, { avatarImageUrl: undefined })}
                />
              </div>
              <span className="text-xs font-medium text-foreground/80 max-w-[96px] text-center leading-tight line-clamp-2">
                {member.name}
              </span>
            </motion.button>
          );
        })}
      </div>

      {/* Backdrop */}
      <AnimatePresence>
        {selected && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/25"
            onClick={() => setSelectedId(null)}
          />
        )}
      </AnimatePresence>

      {/* Bottom sheet */}
      <AnimatePresence>
        {selected && (
          <motion.div
            key="sheet"
            role="dialog"
            aria-modal="true"
            aria-label={selected.name}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            className="fixed bottom-0 inset-x-0 z-50 bg-card rounded-t-3xl shadow-2xl max-w-lg mx-auto overflow-hidden"
            style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom) + 0.5rem)' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/25" />
            </div>

            {/* Close */}
            <button
              onClick={() => setSelectedId(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Schließen"
            >
              <X size={16} />
            </button>

            <div className="px-6 py-4 space-y-5 overflow-y-auto max-h-[65dvh]">
              {/* Portrait + name */}
              <div className="flex flex-col items-center gap-3 pt-1">
                <div className="relative">
                  <CharacterPortrait
                    emoji={selected.avatarEmoji}
                    color={selected.avatarColor}
                    avatarImageUrl={selected.avatarImageUrl}
                    size="lg"
                    onImageError={() => updateMember(selected.id, { avatarImageUrl: undefined })}
                  />
                  {generatingAvatarId === selected.id && (
                    <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
                      <RefreshCw size={24} className="text-white animate-spin" />
                    </div>
                  )}
                </div>
                <div className="text-center space-y-0.5">
                  <p className="text-xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                    {selected.name}
                  </p>
                  <p className="text-sm text-muted-foreground">{selected.role}</p>
                </div>
                <button
                  onClick={() => handleRegenerateAvatar(selected.id)}
                  disabled={generatingAvatarId === selected.id}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={12} className={generatingAvatarId === selected.id ? 'animate-spin' : ''} />
                  {selected.avatarImageUrl ? 'Avatar neu generieren' : 'Avatar generieren'}
                </button>
              </div>

              {/* Description */}
              {selected.description && (
                <p className="text-sm leading-relaxed text-foreground/80 text-center">
                  {selected.description}
                </p>
              )}

              {/* Values */}
              {selected.values.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center">
                  {selected.values.map((v) => (
                    <Badge key={v} variant="secondary">
                      {v}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Typical phrases (first one as teaser) */}
              {selected.typicalPhrases.length > 0 && (
                <p className="text-sm italic text-foreground/70 text-center border-t border-border pt-4">
                  „{selected.typicalPhrases[0]}"
                </p>
              )}

              {/* Detail link */}
              <div className="flex justify-center pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    setSelectedId(null);
                    navigate(routes.innerTeam.memberDetail(selected.id));
                  }}
                >
                  Alle Details
                  <ArrowRight size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LoginPromptDialog open={loginPromptOpen} onOpenChange={setLoginPromptOpen} />
      <VoucherPromptDialog open={voucherPromptOpen} onOpenChange={setVoucherPromptOpen} />
    </>
  );
}
